import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import type React from 'react';
import { forwardRef, type RefObject } from 'react';

import { cn } from '~/utils/cn.ts';

type ScrollAreaProps = React.ComponentProps<typeof ScrollAreaPrimitive.Root> & {
	classNames?: {
		root?: string;
		thumb?: string;
		viewport?: string;
	};
	styles?: {
		root?: React.CSSProperties;
		viewport?: React.CSSProperties;
	};
	custom?: {
		horizontalPaddingLeft?: number;
		horizontalPaddingRight?: number;
		verticalPaddingBottom?: number;
		verticalPaddingTop?: number;
	};
	orientation?: 'vertical' | 'horizontal' | 'both';
	/** Ref to the viewport element - useful for TanStack Virtual integration */
	viewportRef?: RefObject<HTMLDivElement | null>;
};

const ScrollArea = forwardRef<HTMLDivElement, ScrollAreaProps>(
	({ children, className, classNames, styles, custom, orientation = 'vertical', viewportRef, ...props }, ref) => {
		return (
			<ScrollAreaPrimitive.Root
				className={cn('relative overflow-auto', className, classNames?.root)}
				data-slot="scroll-area"
				hidden={false}
				ref={ref}
				style={styles?.root}
				{...props}
			>
				<ScrollAreaPrimitive.Viewport
					className={cn('rounded-[inherit]', 'h-full', classNames?.viewport)}
					ref={viewportRef}
					style={styles?.viewport}
				>
					{children}
				</ScrollAreaPrimitive.Viewport>
				{orientation === 'both' ? (
					<>
						<ScrollBar
							classNameThumb={classNames?.thumb}
							customVerticalPaddingBottom={custom?.verticalPaddingBottom}
							customVerticalPaddingTop={custom?.verticalPaddingTop}
							orientation={'vertical'}
						/>
						<ScrollBar
							classNameThumb={classNames?.thumb}
							customHorizontalPaddingLeft={custom?.horizontalPaddingLeft}
							customHorizontalPaddingRight={custom?.horizontalPaddingRight}
							orientation={'horizontal'}
						/>
						<ScrollAreaPrimitive.Corner className="bg-muted" />
					</>
				) : (
					<>
						<ScrollBar classNameThumb={classNames?.thumb} orientation={orientation} />
						<ScrollAreaPrimitive.Corner className="bg-muted" />
					</>
				)}
			</ScrollAreaPrimitive.Root>
		);
	},
);

ScrollArea.displayName = 'ScrollArea';

function ScrollBar({
	className,
	classNameThumb,
	customHorizontalPaddingLeft,
	customHorizontalPaddingRight,
	customVerticalPaddingBottom,
	customVerticalPaddingTop,
	orientation = 'vertical',
	...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.ScrollAreaScrollbar> & {
	classNameThumb?: string;
	customHorizontalPaddingLeft?: number;
	customHorizontalPaddingRight?: number;
	customVerticalPaddingBottom?: number;
	customVerticalPaddingTop?: number;
}) {
	return (
		<ScrollAreaPrimitive.ScrollAreaScrollbar
			className={cn(
				'flex',
				'select-none',
				'touch-none',
				'p-[2px]',
				'z-30',
				'transition-colors',
				orientation === 'vertical' && 'w-2.5',
				orientation === 'horizontal' && 'h-2.5 flex-col',
				className,
			)}
			data-slot="scroll-bar"
			orientation={orientation}
			{...props}
			style={{
				...props.style,
				marginBottom:
					typeof customVerticalPaddingBottom === 'number' ? customVerticalPaddingBottom : props.style?.bottom,
				marginLeft: typeof customHorizontalPaddingLeft === 'number' ? customHorizontalPaddingLeft : props.style?.left,
				marginRight:
					typeof customHorizontalPaddingRight === 'number' ? customHorizontalPaddingRight : props.style?.right,
				marginTop: typeof customVerticalPaddingTop === 'number' ? customVerticalPaddingTop : props.style?.top,
			}}
		>
			<ScrollAreaPrimitive.ScrollAreaThumb
				// Lyra style: sharp corners for scroll thumb
				className={cn('relative flex-1 rounded-none bg-border', classNameThumb)}
				data-slot="scroll-thumb"
			/>
		</ScrollAreaPrimitive.ScrollAreaScrollbar>
	);
}

export { ScrollArea, ScrollBar };
